import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import StandardPDFLayout from "./StandardPDFLayout";
import type { FormData } from "../types/consultation";
import type { Medication } from "../types/medication";
import type { Analyse } from "../types/analyse";

interface ConsultationSummaryFormPDFProps {
  formData: FormData;
  medications: Medication[];
  analyses: Analyse[];
}

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 8,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  container: { flex: 1, flexDirection: "column", justifyContent: "space-between" },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 5, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: "#113285", borderBottomStyle: "solid" },
  logo: { width: 120, height: 42 },
  headerText: { textAlign: "right" },
  title: { fontSize: 8, fontWeight: "bold", color: "#113285" },
  infoRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 4, marginBottom: 5 },
  infoText: { fontSize: 8, color: "#111827" },
  infoStrong: { fontSize: 9, color: "#113285", fontWeight: "bold" },
  section: { marginBottom: 5 },
  sectionTitle: { fontSize: 9, fontWeight: "bold", marginBottom: 3, color: "#113285", borderBottomWidth: 1, borderBottomColor: "#e5e7eb", borderBottomStyle: "solid", paddingBottom: 2 },
  row: { flexDirection: "row", marginBottom: 1 },
  label: { width: "36%", fontWeight: "bold", color: "#111827", fontSize: 7 },
  value: { width: "64%", color: "#374151", fontSize: 7 },
  listItem: { marginBottom: 1, color: "#374151", fontSize: 7 },
  footer: { marginTop: 8, paddingTop: 5, borderTopWidth: 1, borderTopColor: "#e5e7eb", borderTopStyle: "solid", textAlign: "center", fontSize: 6, color: "#6b7280" },
  watermark: { position: "absolute", opacity: 0.08, width: 200, height: 200, left: 20, top: 120 },
});

const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? dateString : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const ConsultationSummaryFormContent: React.FC<ConsultationSummaryFormPDFProps> = ({ formData, medications, analyses }) => {
  const p = formData.patientInfo;

  
  

  const medsLine = (medications || []).map((m, i) => {
    const parts: string[] = [];
    if (m.dose && m.doseUnit) parts.push(`${m.dose} ${m.doseUnit}`);
    if (m.frequency && m.interval) parts.push(`${m.frequency} ${m.interval}`);
    if (m.duration && m.durationUnit) parts.push(`${m.duration} ${m.durationUnit}`);
    return `${i + 1}. ${m.name}${parts.length ? ' (' + parts.join(', ') + ')' : ''}`;
  }).join('; ');

  return (
    <StandardPDFLayout
      title="RÉCAPITULATIF DE LA CONSULTATION"
      documentDate={formData.consultationActuelle.date}
    >

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>INFORMATIONS PATIENT</Text>
        <View style={styles.row}><Text style={styles.label}>Nom complet:</Text><Text style={styles.value}>{p.prenom} {p.nom}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Date de naissance:</Text><Text style={styles.value}>{formatDate(p.dateNaissance || p.dateOfBirth)}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Téléphone:</Text><Text style={styles.value}>{p.telephone}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Adresse:</Text><Text style={styles.value}>{p.adresse}</Text></View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>CONSTANCES</Text>
        <View style={styles.row}><Text style={styles.label}>Poids:</Text><Text style={styles.value}>{formData.poids}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Taille:</Text><Text style={styles.value}>{formData.taille}</Text></View>
        <View style={styles.row}><Text style={styles.label}>TA:</Text><Text style={styles.value}>{formData.tensionArterielle}</Text></View>
        <View style={styles.row}><Text style={styles.label}>RC:</Text><Text style={styles.value}>{formData.rythmeCardiaque}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Température:</Text><Text style={styles.value}>{formData.temperature}</Text></View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>ÉVALUATION</Text>
        <View style={styles.row}><Text style={styles.label}>Observations:</Text><Text style={styles.value}>{formData.consultationActuelle.observations || ''}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Diagnostics:</Text><Text style={styles.value}>{formData.consultationActuelle.diagnostics || ''}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Recommandations:</Text><Text style={styles.value}>{formData.consultationActuelle.recommandations || ''}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Traitements:</Text><Text style={styles.value}>{formData.consultationActuelle.traitements || ''}</Text></View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>CONDITIONS MÉDICALES</Text>
        <View style={styles.row}><Text style={styles.label}>Durée des symptômes:</Text><Text style={styles.value}>{p.dureeSymptomes || 'Non spécifiée'}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Allergies connues:</Text><Text style={styles.value}>{p.allergies || 'Aucune'}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Médicaments actuels:</Text><Text style={styles.value}>{formData.medicaments || 'Aucun'}</Text></View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>ORDONNANCE</Text>
        <Text style={styles.listItem}>{medsLine || 'Aucune'}</Text>
      </View>

      {formData.informationGynecologique && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INFORMATION GYNÉCOLOGIQUE</Text>

          {/* Informations Générales */}
          <View style={styles.row}>
            <Text style={styles.label}>DDR:</Text>
            <Text style={styles.value}>{formatDate(formData.informationGynecologique.dateDernierRegle) || 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Cycle:</Text>
            <Text style={styles.value}>{formData.informationGynecologique.cycleTousLesJours ? `${formData.informationGynecologique.cycleTousLesJours} jours` : 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Historique:</Text>
            <Text style={styles.value}>
              G:{formData.informationGynecologique.nombreGrossesses || 0} /
              P:{formData.informationGynecologique.nombreEnfants || 0} /
              A:{formData.informationGynecologique.nombreFausseCouches || 0} /
              D:{formData.informationGynecologique.nombreEnfantsDecedes || 0}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Contraception:</Text>
            <Text style={styles.value}>{formData.informationGynecologique.methodeContraceptiveActuelle || 'Aucune'}</Text>
          </View>

          {/* Grossesse Actuelle (si applicable) */}
          {formData.informationGynecologique.grossesseActuelle && (
            <>
              <Text style={{ ...styles.label, marginTop: 4, marginBottom: 2, textDecoration: 'underline' }}>Grossesse actuelle:</Text>
              <View style={styles.row}>
                <Text style={styles.label}>DPA:</Text>
                <Text style={styles.value}>{formatDate(formData.informationGynecologique.grossesseActuelle.dateAccouchementPrevu) || 'N/A'}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Groupe Sanguin:</Text>
                <Text style={styles.value}>
                  {formData.informationGynecologique.grossesseActuelle.groupeSanguin || ''}
                  {formData.informationGynecologique.grossesseActuelle.rh || ''}
                </Text>
              </View>
              {formData.informationGynecologique.grossesseActuelle.nombreFoetus && parseInt(formData.informationGynecologique.grossesseActuelle.nombreFoetus) > 1 && (
                <View style={styles.row}>
                  <Text style={styles.label}>Type:</Text>
                  <Text style={styles.value}>Grossesse multiple ({formData.informationGynecologique.grossesseActuelle.nombreFoetus})</Text>
                </View>
              )}
            </>
          )}
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>ANALYSES COMPLÉMENTAIRES</Text>
        {analyses && analyses.length > 0 ? (
          analyses.map((a, i) => (
            <Text key={i} style={styles.listItem}>{i + 1}. {a.label}</Text>
          ))
        ) : (
          <Text style={styles.listItem}>Aucune</Text>
        )}
      </View>

          {/* Informations gynécologiques */}
          {formData.informationGynecologique && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>INFORMATIONS GYNÉCOLOGIQUES</Text>
              {formData.informationGynecologique.dateDernierRegle && (
                <View style={styles.row}><Text style={styles.label}>Dernières règles:</Text><Text style={styles.value}>{formData.informationGynecologique.dateDernierRegle}</Text></View>
              )}
              {formData.informationGynecologique.cycleTousLesJours && (
                <View style={styles.row}><Text style={styles.label}>Durée du cycle:</Text><Text style={styles.value}>{formData.informationGynecologique.cycleTousLesJours} jours</Text></View>
              )}
              {formData.informationGynecologique.nombreGrossesses && (
                <View style={styles.row}><Text style={styles.label}>Nb grossesses:</Text><Text style={styles.value}>{formData.informationGynecologique.nombreGrossesses}</Text></View>
              )}
              {formData.informationGynecologique.nombreEnfants && (
                <View style={styles.row}><Text style={styles.label}>Nb enfants:</Text><Text style={styles.value}>{formData.informationGynecologique.nombreEnfants} (vivants: {formData.informationGynecologique.nombreEnfantsVivants || '0'})</Text></View>
              )}
              {formData.informationGynecologique.typeAccouchement && (
                <View style={styles.row}><Text style={styles.label}>Type accouchement:</Text><Text style={styles.value}>{formData.informationGynecologique.typeAccouchement}</Text></View>
              )}
              {formData.informationGynecologique.methodeContraceptiveActuelle && (
                <View style={styles.row}><Text style={styles.label}>Contraception:</Text><Text style={styles.value}>{formData.informationGynecologique.methodeContraceptiveActuelle}</Text></View>
              )}

              {/* Suivi de grossesse */}
              {formData.informationGynecologique.grossesseActuelle && (
                <View style={{ marginTop: 4, paddingTop: 3, borderTopWidth: 1, borderTopColor: '#e5e7eb' }}>
                  <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#be185d', marginBottom: 2 }}>SUIVI DE GROSSESSE</Text>
                  {formData.informationGynecologique.grossesseActuelle.dateDebutGrossesse && (
                    <View style={styles.row}><Text style={styles.label}>Début grossesse:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.dateDebutGrossesse}</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.dateAccouchementPrevu && (
                    <View style={styles.row}><Text style={styles.label}>Date prévue:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.dateAccouchementPrevu}</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.groupeSanguin && (
                    <View style={styles.row}><Text style={styles.label}>Groupe sanguin:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.groupeSanguin} {formData.informationGynecologique.grossesseActuelle.rh}</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.nombreFoetus && (
                    <View style={styles.row}><Text style={styles.label}>Nb foetus:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.nombreFoetus}</Text></View>
                  )}
                  <View style={styles.row}><Text style={styles.label}>Grossesse désirée:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.grossesseDesiree ? 'Oui' : 'Non'}</Text></View>
                  {formData.informationGynecologique.grossesseActuelle.diabeteGestationnel && (
                    <View style={styles.row}><Text style={{ ...styles.label, color: '#ea580c' }}>Diabète gestationnel:</Text><Text style={{ ...styles.value, color: '#ea580c' }}>Oui</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.hypertensionArterielle && (
                    <View style={styles.row}><Text style={{ ...styles.label, color: '#ea580c' }}>HTA:</Text><Text style={{ ...styles.value, color: '#ea580c' }}>Oui</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.preeclampsie && (
                    <View style={styles.row}><Text style={{ ...styles.label, color: '#dc2626' }}>Prééclampsie:</Text><Text style={{ ...styles.value, color: '#dc2626' }}>Oui</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.traitementEnCours && (
                    <View style={styles.row}><Text style={styles.label}>Traitement:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.traitementEnCours}</Text></View>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.commentaires && (
                    <View style={styles.row}><Text style={styles.label}>Commentaires:</Text><Text style={styles.value}>{formData.informationGynecologique.grossesseActuelle.commentaires}</Text></View>
                  )}
                </View>
              )}
            </View>
          )}

    </StandardPDFLayout>
  );
};

const ConsultationSummaryFormPDF: React.FC<ConsultationSummaryFormPDFProps> = (props) => (
  <Document>
    <Page size="A5" style={styles.page}>
      <ConsultationSummaryFormContent {...props} />
    </Page>
  </Document>
);

export default ConsultationSummaryFormPDF;
